/*!
 * egt.journal.views — The Making, the founder's journal at /journal (spec 35 WP3;
 * approved design: founders-journal the-making-v6.template.html, D023–D032).
 *
 * Deliberately a little separate from the rest of the site (D035): its own navbar,
 * reduced footer, Sepia default with Paper / Dim / Night / Auto, a cross-fading
 * photograph behind home and Discover, a solid reading sheet for entries, and
 * context focus dialogs (theme, subscribe, filters, images). It still runs on the
 * platform: the content store, capture, schema and the site's analytics.
 *
 * Every class is prefixed tm- under one .tm root (app/scss/egt.journal.scss) so
 * nothing collides with the site's own classes. Server-rendered UA/JSON: every
 * page reads with JavaScript off; /src/egt.journal.js adds the dialogs, the live
 * search, the background cross-fade, the reveal and "Not now".
 *
 *   home      /journal, /journal/page/<n>
 *   discover  /journal/discover?q=&subject=&series=&type=&len=&sort=
 *   entry     /journal/<slug>
 */
var S = require("./egt.sections.js");

var TZ = "America/New_York";
var PER_PAGE = 15;
var AUTHOR_IMG = "/imgs/founder/justin.jpg";
var TAGLINE = "Justin Kazmierczak's journal on building EGT: the studio, the films and the Universe.";

/* Subject keys come from the entry schema; names and colours from the approved
 * template (names are placeholders until the founder renames them, QN050). */
var SUBJECTS = [
  { key: "building", name: "Building the platform", color: "#C48E2F", img: "luke-lung-03UCoidYvXw" },
  { key: "film", name: "Filmmaking with AI", color: "#2F9C86", img: "piotr-chrobot-6oUsyeYXgTg" },
  { key: "faith", name: "Faith & purpose", color: "#8E73D6", img: "robert-lukeman-PH0HYjsf2n8" },
  { key: "eng", name: "Engineering notes", color: "#8A9A2E", img: "egor-komarov-io5clS15h9Q" },
  { key: "solo", name: "Running a solo studio", color: "#A08C6A", img: "copernico-p_kICQCOM4s" },
  { key: "story", name: "The Universe stories", color: "#6E9E6A", img: "paige-cody-Y4b9JXKINNY" }
];
exports.SUBJECTS = SUBJECTS;
var SUBJ = {};
SUBJECTS.forEach(function (s) { SUBJ[s.key] = s; });

/* The platform's Unsplash set (never the main site's images, D028). */
/* The platform's 18 approved Unsplash backgrounds (platform agent-task-tracker/unsplash,
 * same set as accounts' platform_background catalog). One is picked on the SERVER for
 * every page view (app/routes/egt.content.pages.js freshBackground), like the platform
 * dashboard: a fresh image each visit, never swapped live in the browser. */
var BG = [ "charles-forerunner-3fPXt37X6UQ", "copernico-p_kICQCOM4s", "david-rodrigo-Fr6zexbmjmc", "derek-thomson-TWoL-QCZubY",
  "egor-komarov-io5clS15h9Q", "gregory-brainard-0Nwc96Vj-fo", "harshil-gudka-77zGnfU_SFU", "jean-philippe-delberghe-zZvYBYY70uo",
  "karsten-winegeart-0Wra5YYVQJE", "logan-weaver-lgnwvr-Apejl7P4-vk", "luke-lung-03UCoidYvXw", "paige-cody-Y4b9JXKINNY",
  "piotr-chrobot-6oUsyeYXgTg", "quino-al-vmZ2DehWQ3Q", "robert-lukeman-PH0HYjsf2n8", "saiph-muhammad-CSs8aiN_LkI",
  "sawyer-bengtson-tnv84LOjes4", "valdemaras-d-lZF0XDiW2Jo" ];
exports.BACKGROUNDS = BG;
/* Rendered pages carry this token where the background goes; the route swaps in the pick. */
var BG_TOKEN = "__tm_bg__";
exports.BG_TOKEN = BG_TOKEN;
function bgUrl(name) { return "/imgs/journal/" + name + ".jpg"; }
var FALLBACK_ART = bgUrl("luke-lung-03UCoidYvXw");

var TYPES = [ "Essay", "Making-of", "Field note" ];
var LENS = [ [ "short", "Under 5 min" ], [ "mid", "5–10 min" ], [ "long", "Over 10 min" ] ];
var SORTS = [ [ "new", "Newest" ], [ "old", "Oldest" ], [ "long", "Longest read" ] ];

/* ── small helpers ────────────────────────────────────────────────────────── */

function subjName(k) { return (SUBJ[k] && SUBJ[k].name) || String(k || ""); }
function subjColor(k) { return (SUBJ[k] && SUBJ[k].color) || "#C48E2F"; }
function seriesName(key) {
  return String(key || "").split("-").filter(Boolean).map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(" ");
}

/** "2026-10-09" or an instant → "Oct 9, 2026" (calendar dates stay calendar dates). */
function fdate(v) {
  if (!v) { return ""; }
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
  var d = m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12)) : new Date(v);
  if (isNaN(d.getTime())) { return ""; }
  return d.toLocaleDateString("en-US", { timeZone: m ? "UTC" : TZ, month: "short", day: "numeric", year: "numeric" });
}
function isoDay(v) {
  if (!v) { return ""; }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) { return String(v); }
  var d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-CA", { timeZone: TZ });   // same calendar day fdate shows
}
exports.fdate = fdate;

function site(u, fallback) { return (typeof u === "string" && /^\/(?![\/\\])[^\s"'<>\\]*$/.test(u)) ? u : (fallback || FALLBACK_ART); }

/** Inline markup from the schema: *em*, **strong**, [text](https://… or /path). Everything else is text. */
/* "{edit date}" (or the handoff's "{edit date, set at publish}") is filled with the
 * date the entry went live, set by renderEntry before the body renders. */
var EDIT_DATE = "";
function inline(text) {
  var s = String(text || "").replace(/\{edit date(?:, set at publish)?\}/gi, EDIT_DATE || "on republication");
  var out = [];
  var re = /\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(((?:https:\/\/|\/(?![\/\\]))[^\s)"'<>\\]*)\)/g;
  var last = 0, m;
  while ((m = re.exec(s))) {
    if (m.index > last) { out.push(s.slice(last, m.index)); }
    if (m[1] !== undefined) { out.push({ n: "strong", i: m[1] }); }
    else if (m[2] !== undefined) { out.push({ n: "em", i: m[2] }); }
    else {
      var a = { n: "a", href: m[4], i: m[3] };
      if (/^https:/.test(m[4])) { a.target = "_blank"; a.rel = "noopener"; }
      out.push(a);
    }
    last = re.lastIndex;
  }
  if (last < s.length) { out.push(s.slice(last)); }
  return out.length === 1 && typeof out[0] === "string" ? out[0] : out;
}
exports.inline = inline;

var ICON = {
  book: '<path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M4 19V5"/>',
  stars: '<circle cx="5" cy="7" r="1.6" fill="currentColor"/><circle cx="12" cy="4" r="1.6" fill="currentColor"/><circle cx="19" cy="9" r="1.6" fill="currentColor"/><circle cx="9" cy="14" r="1.6" fill="currentColor"/><circle cx="17" cy="19" r="1.6" fill="currentColor"/><path d="M5 7l7-3 7 5-10 5 8 5"/>',
  clock: '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>',
  spark: '<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18"/>',
  play: '<circle cx="12" cy="12" r="9"/><path d="m10 8 6 4-6 4z" fill="currentColor"/>'
};
/* Icons are fixed strings from this file, parsed into nodes (never from content). */
function svgNodes(markup) {
  var nodes = [];
  var re = /<(path|circle|rect)\s([^>]*?)\/>/g, m;
  while ((m = re.exec(markup))) {
    var node = { n: m[1] }, ar = /([a-z-]+)="([^"]*)"/g, a;
    while ((a = ar.exec(m[2]))) { node[a[1]] = a[2]; }
    nodes.push(node);
  }
  return nodes;
}
function icon(name, cls) {
  return { n: "svg", c: cls || "", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "1.8",
    "aria-hidden": "true", focusable: "false", i: svgNodes(ICON[name] || "") };
}
function iw(name) { return { n: "span", c: "tm-iw", i: icon(name) }; }
function searchIcon() {
  return { n: "svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", "aria-hidden": "true", focusable: "false",
    i: [ { n: "circle", cx: "11", cy: "11", r: "7" }, { n: "path", d: "m20 20-3.5-3.5" } ] };
}
function themeIcon() {
  return { n: "svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "aria-hidden": "true", focusable: "false",
    i: [ { n: "circle", cx: "12", cy: "12", r: "8.5" }, { n: "path", d: "M12 3.5a8.5 8.5 0 0 0 0 17z", fill: "currentColor" } ] };
}
function expandIcon() {
  return { n: "svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", "aria-hidden": "true", focusable: "false",
    i: { n: "path", d: "M4 9V4h5M20 15v5h-5M15 4h5v5M9 20H4v-5" } };
}
function logo(cls) { return { n: "img", c: cls || "tm-logo", src: "/logo.svg", alt: "", width: "34", height: "34" }; }

function chipLink(k) { return { n: "a", c: "tm-chip", rel: "tag", href: "/journal/discover?subject=" + encodeURIComponent(k), style: "--c:" + subjColor(k), i: [ { n: "i" }, subjName(k) ] }; }
function chipTag(k) { return { n: "span", c: "tm-chip", style: "--c:" + subjColor(k), i: [ { n: "i" }, subjName(k) ] }; }

/* ── the shell ────────────────────────────────────────────────────────────── */

/* Applied before first paint so the chosen theme never flashes (D024). */
var PREPAINT = "try{var t=localStorage.getItem('tm-theme')||'sepia';if(t!=='auto'){document.documentElement.setAttribute('data-theme',t)}}catch(e){document.documentElement.setAttribute('data-theme','sepia')}" +
  /* reveal only when script runs; if the journal script never arrives, show everything */
  "(function(r){if(!r)return;r.classList.add('tm-js');setTimeout(function(){if(!window.__tmReady){r.querySelectorAll('.tm-reveal').forEach(function(e){e.classList.add('is-in')})}},4000)})(document.currentScript&&document.currentScript.parentNode);";

function banner(b) {
  if (!b || !b.text) { return null; }
  return { n: "div", c: "egt-cpreview egt-cpreview--" + (b.tone || "info"), role: "status",
    i: [ { n: "span", c: "egt-cpreview__dot", "aria-hidden": "true" }, { n: "span", c: "egt-cpreview__text", i: b.text } ] };
}

// small home icon left of the logo: back to egt.studio (the logo itself goes to the journal home)
function homeIcon() {
  return { n: "svg", viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", i: { n: "path", d: "M4 11l8-7 8 7M6.5 9.5V20h4v-5.5h3V20h4V9.5", fill: "none", stroke: "currentColor", "stroke-width": "1.9", "stroke-linecap": "round", "stroke-linejoin": "round" } };
}

function header(opts) {
  return { n: "header", c: "tm-mh tm-glass", i: { n: "div", c: "tm-wrap", i: [
    { n: "a", c: "tm-backbtn", href: "/", "aria-label": "EGT home", title: "EGT home", i: homeIcon() },
    { n: "a", c: "tm-home", id: "tm-home", href: "/journal", "aria-label": "The Making, home", i: [
      logo(),
      { n: "span", c: "tm-wm", i: [ "The ", { n: "em", i: "Making" } ] }
    ] },
    { n: "nav", c: "tm-mnav", "aria-label": "Journal", i: [
      { n: "button", c: "tm-btn tm-sub", type: "button", id: "tm-sub-btn", "aria-haspopup": "dialog", i: "Subscribe" },
      { n: "a", c: "tm-circ", href: "/journal/discover", "aria-label": "Search and discover", i: searchIcon() },
      { n: "button", c: "tm-circ", type: "button", id: "tm-theme-btn", "aria-label": "Reading theme", "aria-haspopup": "dialog", i: themeIcon() }
    ] }
  ] } };
}

function footer() {
  var year = (new Date()).getFullYear();
  function list(items) {
    return { n: "ul", i: items.map(function (it) {
      var a = { n: "a", href: it[1], i: it[0] };
      if (/^https:/.test(it[1])) { a.target = "_blank"; a.rel = "noopener"; }
      if (it[2]) { a["data-tm-open"] = it[2]; }
      return { n: "li", i: a };
    }) };
  }
  return { n: "footer", c: "tm-ft", "aria-label": "Footer", i: { n: "div", c: "tm-inner tm-glass", i: { n: "div", c: "tm-card", i: [
    { n: "div", c: "tm-b", i: [ logo("tm-logo tm-ft-logo"), { n: "div", i: [
      { n: "b", c: "tm-ft-wm", i: [ "The ", { n: "em", i: "Making" } ] },
      { n: "p", i: TAGLINE } ] } ] },
    { n: "div", i: [ { n: "h5", c: "tm-kicker", i: "The Making" }, list([
      [ "Home", "/journal" ], [ "Discover", "/journal/discover" ], [ "Subscribe", "/journal#subscribe", "subscribe" ], [ "RSS", "/journal/feed.xml" ] ]) ] },
    { n: "div", i: [ { n: "h5", c: "tm-kicker", i: "EGT" }, list([
      [ "egt.studio", "/" ], [ "The Universe Platform", "/platform" ], [ "Join the beta", "/reserve" ],
      [ "AI Film Studio", "/studio" ], [ "Forge, custom builds", "/forge" ], [ "Exploit", "/exploit" ] ]) ] },
    { n: "div", i: [ { n: "h5", c: "tm-kicker", i: "Listen & watch" }, list([
      [ "Create_ podcast", "/podcasts/create" ], [ "All podcasts", "/podcasts" ],
      [ "The Journey of the Coin", "/entertainment/the-journey-of-the-coin" ], [ "YouTube", "https://youtube.com/@egtuniverse" ] ]) ] },
    { n: "div", c: "tm-codex", i: [
      { n: "a", href: "/legal/codex", i: "Terms of Service" },
      { n: "a", href: "/legal/codex#sec-part-3-privacy-and-data", i: "Privacy Policy" },
      { n: "span", i: [ "© " + year + " EGT. All rights reserved. Subject to the terms of service in ", { n: "a", href: "/legal/codex", i: "The Codex" }, "." ] },
      S.trademarkNotice() ? { n: "span", c: "tm-tmk", i: S.trademarkNotice() } : null ].filter(Boolean) }
  ] } } };
}

function themeDialog() {
  var themes = [ [ "sepia", "Sepia", "Default", "#E8DCC5", "#120D06" ], [ "paper", "Paper", "Softer light", "#EAE5DB", "#0B0A08" ],
    [ "dim", "Dim", "Low glare", "#2A2825", "#F4EFE6" ], [ "night", "Night", "Dark", "#0C0C0C", "#F0ECE4" ],
    [ "auto", "Auto", "Follows your device", "linear-gradient(135deg,#E8DCC5 50%,#0C0C0C 50%)", "#C48E2F" ] ];
  return { n: "dialog", c: "tm-cf", id: "tm-theme-dlg", "aria-labelledby": "tm-tdh", i: { n: "div", c: "tm-cfi", i: [
    { n: "h2", id: "tm-tdh", i: "Reading theme" },
    { n: "p", c: "tm-sub", i: "One tap applies it. Remembered on this device. On a very bright screen, Dim helps most." },
    { n: "div", c: "tm-keys", i: themes.map(function (t) {
      return { n: "button", c: "tm-key", type: "button", "data-t": t[0], "aria-pressed": "false", i: [
        { n: "span", c: "tm-orb", style: "background:" + t[3] + ";color:" + t[4], i: { n: "span", c: "tm-orb-aa", i: "Aa" } },
        t[1], { n: "small", i: t[2] } ] };
    }) },
    { n: "div", c: "tm-actions", i: { n: "button", c: "tm-pill", type: "button", "data-close": "true", i: "Done" } },
    { n: "button", c: "tm-circ tm-x", type: "button", "data-close": "true", "aria-label": "Close", i: "✕" }
  ] } };
}

function subscribeDialog(offer) {
  return { n: "dialog", c: "tm-cf", id: "tm-sub-dlg", "aria-labelledby": "tm-subh", i: { n: "div", c: "tm-cfi", i: [
    { n: "h2", id: "tm-subh", i: "Subscribe" },
    { n: "p", c: "tm-sub", i: "Letters from Justin about building EGT, the films and the Universe. Unsubscribe any time." },
    { n: "div", c: "tm-subbody", i: captureForm("tm-cap-nav", offer, "dialog") },
    { n: "button", c: "tm-circ tm-x", type: "button", "data-close": "true", "aria-label": "Close", i: "✕" }
  ] } };
}

function lightbox() {
  return { n: "dialog", c: "tm-cf", id: "tm-lb-dlg", "aria-label": "Image viewer", i: { n: "div", c: "tm-cfi", i: [
    { n: "div", c: "tm-lbimg", i: { n: "img", id: "tm-lb-img", alt: "" } },
    { n: "div", c: "tm-actions", i: [ { n: "span", c: "tm-cap", id: "tm-lb-cap" },
      { n: "button", c: "tm-pill", type: "button", id: "tm-lb-prev", "aria-label": "Previous image", i: "←" },
      { n: "button", c: "tm-pill", type: "button", id: "tm-lb-next", "aria-label": "Next image", i: "→" } ] },
    { n: "button", c: "tm-circ tm-x", type: "button", "data-close": "true", "aria-label": "Close", i: "✕" }
  ] } };
}

function shell(opts, mainNodes, schema) {
  var art = opts.reading ? [] : [ { n: "div", c: "tm-bg", id: "tm-bg", "aria-hidden": "true",
    i: { n: "img", c: "tm-on", src: "/imgs/journal/" + BG_TOKEN + ".jpg", alt: "", fetchpriority: "high", decoding: "async" } } ];
  var b = banner(opts.banner);
  return { n: "div", c: "tm" + (opts.reading ? " tm-reading" : ""), "data-tm-view": opts.view, i: []
    .concat([ { n: "script", i: PREPAINT },
      { n: "link", rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&display=swap" } ])
    .concat(b ? [ b ] : [])
    .concat(art)
    .concat([ { n: "a", c: "tm-skip", href: "#tm-main", i: "Skip to content" }, header(opts),
      { n: "main", id: "tm-main", c: "tm-main", i: mainNodes }, footer(),
      themeDialog(), subscribeDialog(opts.offer), lightbox(),
      { n: "script", src: "/src/egt.capture.js", type: "text/javascript", defer: "defer" },
      { n: "script", src: "/src/egt.countdown.js", type: "text/javascript", defer: "defer" },
      { n: "script", src: "/src/egt.journal.js", type: "text/javascript", defer: "defer" },
      { n: "script", src: "/src/egt.scrollhint.js", type: "text/javascript", defer: "defer" },
      S.siteSchema() ])
    .concat(schema ? [ schema ] : []) };
}

/* ── offers (CTA registry, D023/D027) ─────────────────────────────────────── */

var DEFAULT_OFFER = { id: "email-default", type: "email", ends: null, fallback: null, icon: "mail",
  kicker: "Get new entries by email", headline: "If this part was useful, the next entry lands in your inbox.",
  body: "Letters about building EGT, the films and the Universe.", button: "Send me new entries",
  capture_source: "journal" };

function todayET() { return new Date().toLocaleDateString("en-CA", { timeZone: TZ }); }
exports.todayET = todayET;

/** Follows the fallback chain past expired offers. An ebook offer is shown only
 * once ebook delivery exists; until then it falls back too (never promise what
 * the capture does not send). */
function resolveOffer(registry, id) {
  var reg = registry || {};
  var today = todayET();
  var c = reg[id], hops = 0;
  function usable(x) { return x && (!x.ends || x.ends >= today) && x.type !== "ebook"; }
  while (c && !usable(c) && hops < 6) { c = reg[c.fallback]; hops++; }
  if (!usable(c)) { c = usable(reg["email-default"]) ? reg["email-default"] : DEFAULT_OFFER; }
  return c;
}
exports.resolveOffer = resolveOffer;

function captureForm(id, offer, placement) {
  offer = offer || DEFAULT_OFFER;
  if (offer.type === "waitlist") {
    return { n: "div", c: "tm-capform", i: [
      { n: "a", c: "tm-btn", href: "/reserve", "data-tm-cta": offer.id, "data-tm-placement": placement, i: [ offer.button, " →" ] } ] };
  }
  var src = /^[a-z0-9-]+$/.test(String(offer.capture_source || "")) ? offer.capture_source : "journal";
  return { n: "form", c: "tm-capform egt-cap", id: id + "-form", novalidate: "novalidate", "data-egt-capture": "true",
    "data-egt-capture-source": src, "data-tm-cta": offer.id, "data-tm-placement": placement, i: [
    { n: "label", c: "tm-v", for: id + "-email", i: "Email address" },
    { n: "div", c: "tm-caprow", i: [
      { n: "input", id: id + "-email", type: "email", name: "email", required: "required", autocomplete: "email", inputmode: "email",
        spellcheck: "false", placeholder: "you@example.com", "aria-describedby": id + "-note" },
      { n: "button", c: "tm-btn", type: "submit", "data-egt-capture-submit": "true", i: offer.button } ] },
    { n: "label", c: "tm-opt", i: [
      { n: "input", type: "checkbox", name: "consent", value: "yes", required: "required", "data-egt-capture-consent": "true" },
      " Yes, email me new entries and news from EGT. I can unsubscribe from any email." ] },
    { n: "div", c: "tm-hp", "aria-hidden": "true", i: [ { n: "label", for: id + "-website", i: "Leave this field empty" },
      { n: "input", id: id + "-website", type: "text", name: "website", value: "", tabindex: "-1", autocomplete: "off" } ] },
    { n: "p", id: id + "-note", c: "tm-ok", role: "status", "aria-live": "polite", "data-egt-capture-note": "true" }
  ] };
}

function ctaBlock(offer, slug) {
  return { n: "aside", c: "tm-cta tm-glass", "data-cta": offer.id, "data-tm-post": slug, "aria-label": offer.kicker, i: { n: "div", c: "tm-card", i: [
    { n: "div", c: "tm-top", i: [ iw(offer.icon === "spark" ? "spark" : offer.icon === "book" ? "book" : "mail"), { n: "span", c: "tm-kicker", i: offer.kicker } ] },
    { n: "p", c: "tm-hl", i: offer.headline },
    { n: "p", c: "tm-bd", i: offer.body },
    captureForm("tm-cap-" + slug.slice(0, 40), offer, "mid"),
    { n: "p", c: "tm-fine", i: [ { n: "span", i: [ "Unsubscribe any time · ", { n: "a", href: "/legal/codex#sec-part-3-privacy-and-data", i: "privacy" } ] },
      { n: "button", type: "button", c: "tm-nn", i: "Not now" } ] }
  ] } };
}

/* ── shared cards ─────────────────────────────────────────────────────────── */

/* A card is a plain container: the title link is stretched over the whole card,
 * and the full-screen button sits above it as a sibling (no control inside a link). */
function storyCard(e, eager) {
  var art = site(e.hero && e.hero.url);
  return { n: "article", c: "tm-st tm-card tm-reveal", i: [
    { n: "figure", i: [
      { n: "img", src: art, alt: "", loading: eager ? "eager" : "lazy", decoding: "async" },
      { n: "button", type: "button", c: "tm-expand", "data-full": art, "data-cap": e.title, "aria-label": "View image full screen", i: expandIcon() } ] },
    { n: "div", c: "tm-in", i: [ { n: "h3", i: { n: "a", c: "tm-st-link", href: e.url, i: e.title } }, { n: "p", i: e.dek || "" } ] },
    { n: "div", c: "tm-tb", i: [
      { n: "span", c: "tm-tbm", i: [ { n: "time", datetime: isoDay(e.date), i: fdate(e.date) }, { n: "span", c: "tm-sep", "aria-hidden": "true" },
        (e.read_minutes || 1) + " min read" ] },
      { n: "span", c: "tm-tbc", i: (e.subjects || []).slice(0, 1).map(chipTag) } ] }
  ] };
}

function sideModules(x) {
  var mods = [];
  if (x.next) {
    mods.push({ n: "div", c: "tm-mod tm-glass", i: { n: "div", c: "tm-card", i: [
      { n: "div", c: "tm-modhead", i: [ iw("clock"), { n: "span", c: "tm-kicker", i: "Next entry" } ] },
      { n: "div", c: "tm-cd", "data-tm-countdown": String(x.next), i: [ [ "d", "days" ], [ "h", "hours" ], [ "m", "min" ], [ "s", "sec" ] ].map(function (u) {
        return { n: "span", c: "tm-well", i: [ { n: "em", c: "egt-cdv", "data-u": u[0], i: "–" }, u[1] ] };
      }) },
      { n: "p", c: "tm-meta tm-cdnote", i: "Lands " + fdate(x.next) + "." }
    ] } });
  }
  if (x.episodes && x.episodes.length) {
    mods.push({ n: "div", c: "tm-mod tm-glass", i: [ { n: "h4", c: "tm-kicker", i: "Latest from Create_" },
      { n: "ul", i: x.episodes.slice(0, 3).map(function (c) {
        return { n: "li", i: { n: "a", c: "tm-ep tm-card", href: c.url_path, i: [
          { n: "img", src: site(c.card_image), alt: "", loading: "lazy", decoding: "async" },
          { n: "span", i: [ c.title, { n: "small", i: [ c.runtime_display, "Create_" ].filter(Boolean).join(" · ") } ] } ] } };
      }) } ] });
  }
  var series = seriesList(x.entries);
  if (series.length) {
    mods.push({ n: "div", c: "tm-mod tm-glass", i: [ { n: "h4", c: "tm-kicker", i: "Series" },
      { n: "ul", i: series.map(function (s) {
        return { n: "li", i: { n: "a", c: "tm-card", href: "/journal/discover?series=" + encodeURIComponent(s.key), i: [
          { n: "span", i: [ s.name, { n: "small", i: s.count + (s.of ? " of " + s.of : "") + " parts" } ] }, { n: "span", "aria-hidden": "true", i: "›" } ] } };
      }) } ] });
  }
  mods.push({ n: "div", c: "tm-mod tm-glass", i: [ { n: "h4", c: "tm-kicker", i: "Subjects" },
    { n: "ul", i: SUBJECTS.map(function (s) {
      var count = (x.entries || []).filter(function (e) { return (e.subjects || []).indexOf(s.key) > -1; }).length;
      return { n: "li", i: { n: "a", c: "tm-card", href: "/journal/discover?subject=" + s.key, i: [
        { n: "span", c: "tm-subjname", i: [ { n: "i", style: "background:" + s.color }, s.name ] }, { n: "span", c: "tm-count", i: String(count) } ] } };
    }) } ] });
  return { n: "aside", c: "tm-side", i: mods };
}

function seriesList(entries) {
  var map = {}, order = [];
  (entries || []).forEach(function (e) {
    if (!e.series || !e.series.key) { return; }
    var k = e.series.key;
    if (!map[k]) { map[k] = { key: k, name: seriesName(k), count: 0, of: e.series.of || null, img: e.hero && e.hero.url }; order.push(k); }
    map[k].count++;
    if (e.series.of) { map[k].of = e.series.of; }
  });
  return order.map(function (k) { return map[k]; });
}

function topics() {
  return { n: "nav", c: "tm-topics tm-glass", "aria-label": "Subjects", "data-scroll-hint": "true", i: SUBJECTS.map(function (s) {
    return { n: "a", href: "/journal/discover?subject=" + s.key, style: "--c:" + s.color, i: [ { n: "i" }, s.name ] };
  }).concat([ { n: "a", href: "/journal/discover", i: "All subjects →" } ]) };
}

function sechead(title, ctrls) {
  return { n: "div", c: "tm-sechead tm-reveal", i: [ { n: "h2", i: title } ].concat(ctrls ? [ ctrls ] : []) };
}

/* ── home ─────────────────────────────────────────────────────────────────── */

function pager(page, pages) {
  if (pages < 2) { return null; }
  function href(p) { return p === 1 ? "/journal" : "/journal/page/" + p; }
  var items = [ page > 1 ? { n: "a", href: href(page - 1), rel: "prev", i: "← Newer" } : { n: "span", c: "tm-dis", i: "← Newer" } ];
  for (var p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || Math.abs(p - page) <= 1) {
      items.push(p === page ? { n: "span", "aria-current": "page", i: String(p) } : { n: "a", href: href(p), i: String(p) });
    } else if (Math.abs(p - page) === 2) { items.push({ n: "span", c: "tm-dis", i: "…" }); }
  }
  items.push(page < pages ? { n: "a", href: href(page + 1), rel: "next", i: "Older →" } : { n: "span", c: "tm-dis", i: "Older →" });
  items.push({ n: "a", c: "tm-all", href: "/journal/discover?sort=new&all=1", i: "All entries, by date" });
  return { n: "nav", c: "tm-pg", "aria-label": "Journal pages", i: items };
}

function homeView(data, bannerInfo) {
  var x = data.extra || {};
  var all = x.entries || [];
  var page = Math.max(1, x.page || 1);
  var pages = all.length <= PER_PAGE ? 1 : 1 + Math.ceil((all.length - PER_PAGE) / PER_PAGE);
  var nodes = [];
  if (page === 1 && all.length) {
    var L = all[0], also = all.slice(1, 4);
    nodes.push({ n: "section", c: "tm-cover", "aria-label": "Latest entry", i: { n: "div", c: "tm-wrap", i: [
      { n: "div", c: "tm-lead tm-glass", i: { n: "div", c: "tm-card tm-reveal", i: [
        { n: "div", c: "tm-row", i: [ iw("book"), { n: "span", c: "tm-kicker", i: "Latest · " + subjName(L.subject) } ] },
        { n: "h1", i: { n: "a", href: L.url, i: L.title } },
        { n: "p", i: L.dek || "" },
        { n: "div", c: "tm-meta tm-leadmeta", i: [ { n: "time", datetime: isoDay(L.date), i: fdate(L.date) }, { n: "span", i: "· " + (L.read_minutes || 1) + " min read" } ]
          .concat((L.subjects || []).map(chipLink)) },
        { n: "a", c: "tm-btn", href: L.url, i: "Read the entry →" }
      ] } },
      also.length ? { n: "div", c: "tm-alsonew tm-glass tm-reveal", i: [ { n: "h2", c: "tm-kicker", i: "Also new" } ].concat(also.map(function (e) {
        return { n: "a", c: "tm-an tm-card", href: e.url, i: [ { n: "img", src: site(e.hero && e.hero.url), alt: "", loading: "lazy", decoding: "async" },
          { n: "span", i: [ { n: "b", i: e.title }, { n: "small", i: fdate(e.date) + " · " + (e.read_minutes || 1) + " min" } ] } ] };
      })) } : ""
    ] } });
  }
  var wrap = [ topics() ];
  var featured = page === 1 ? all.slice(4, 9) : [];
  if (featured.length) {
    wrap.push({ n: "section", c: "tm-sec", i: [
      sechead("Featured", { n: "div", c: "tm-ctrls", i: [
        { n: "button", c: "tm-circ", type: "button", "data-tm-rail": "-1", "aria-label": "Previous", i: "‹" },
        { n: "button", c: "tm-circ", type: "button", "data-tm-rail": "1", "aria-label": "Next", i: "›" } ] }),
      { n: "div", c: "tm-rail", id: "tm-rail", "data-scroll-hint": "true", i: featured.map(function (e) {
        return { n: "a", c: "tm-tile", href: e.url, i: [ { n: "img", src: site(e.hero && e.hero.url), alt: "", loading: "lazy", decoding: "async" },
          { n: "div", c: "tm-cap", i: [ { n: "span", i: subjName(e.subject) }, { n: "b", i: e.title }, { n: "small", i: fdate(e.date) + " · " + (e.read_minutes || 1) + " min" } ] } ] };
      }) }
    ] });
  }
  var items = page === 1 ? all.slice(0, PER_PAGE) : all.slice(PER_PAGE + (page - 2) * PER_PAGE, PER_PAGE + (page - 1) * PER_PAGE);
  var series = seriesList(all);
  var grid = [];
  items.forEach(function (e, i) {
    if (i === 6 && series.length) {
      var s = series[0];
      grid.push({ n: "a", c: "tm-break tm-reveal", href: "/journal/discover?series=" + encodeURIComponent(s.key), i: [
        { n: "img", src: site(s.img), alt: "", loading: "lazy", decoding: "async" },
        { n: "div", c: "tm-cap", i: [ { n: "span", c: "tm-kicker tm-kicker-gold", i: "Series · " + s.count + (s.of ? " of " + s.of : "") + " published" },
          { n: "b", i: s.name }, { n: "p", i: "Best read in order." } ] } ] });
    }
    grid.push(storyCard(e, false));
  });
  wrap.push({ n: "section", c: "tm-sec" + (page === 1 ? " tm-sec-tight" : ""), i: [
    sechead(page === 1 ? "The journal" : "Page " + page + " of " + pages),
    { n: "div", c: "tm-layout", i: [
      { n: "div", c: "tm-paper tm-glass", i: [ { n: "div", c: "tm-grid2", i: grid }, pager(page, pages) ].filter(Boolean) },
      sideModules(x)
    ] }
  ] });
  nodes.push({ n: "div", c: "tm-wrap", i: wrap });
  var O = S.siteOrigin();
  var schema = S.jsonLd([
    { "@type": "Blog", "@id": O + "/journal#blog", url: O + "/journal", name: "The Making", description: TAGLINE,
      inLanguage: "en-US", author: { "@id": O + "/founder#person" }, publisher: { "@id": O + "/#organization" },
      isPartOf: { "@id": O + "/#website" },
      blogPost: items.slice(0, 15).map(function (e) { return { "@type": "BlogPosting", "@id": O + e.url + "#post", url: O + e.url, headline: e.title, datePublished: isoDay(e.date) }; }) },
    { "@type": "BreadcrumbList", itemListElement: [ { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
      { "@type": "ListItem", position: 2, name: "The Making", item: O + "/journal" } ] }
  ]);
  return shell({ view: "home", page: page, banner: bannerInfo, offer: x.navOffer }, nodes, schema);
}

/* ── discover ─────────────────────────────────────────────────────────────── */

function filterEntries(all, f) {
  var q = String(f.q || "").toLowerCase().trim();
  var words = q ? q.split(/\s+/) : [];
  var r = all.filter(function (e) {
    var hay = (e.title + " " + (e.dek || "") + " " + (e.text || "") + " " + (e.subjects || []).map(subjName).join(" ")).toLowerCase();
    for (var i = 0; i < words.length; i++) { if (hay.indexOf(words[i]) < 0) { return false; } }
    if (f.subject && f.subject.length && !f.subject.some(function (s) { return (e.subjects || []).indexOf(s) > -1; })) { return false; }
    if (f.series && (!e.series || e.series.key !== f.series)) { return false; }
    if (f.type && e.type !== f.type) { return false; }
    var m = e.read_minutes || 1;
    if (f.len === "short" && m >= 5) { return false; }
    if (f.len === "mid" && (m < 5 || m > 10)) { return false; }
    if (f.len === "long" && m <= 10) { return false; }
    return true;
  });
  r.sort(function (a, b) {
    if (f.sort === "old") { return isoDay(a.date) < isoDay(b.date) ? -1 : 1; }
    if (f.sort === "long") { return (b.read_minutes || 0) - (a.read_minutes || 0); }
    return isoDay(a.date) < isoDay(b.date) ? 1 : -1;
  });
  return r;
}
exports.filterEntries = filterEntries;

function discoverView(data, bannerInfo) {
  var x = data.extra || {};
  var all = x.entries || [];
  var f = x.filters || {};
  var active = !!(f.q || (f.subject && f.subject.length) || f.series || f.type || f.len || (f.sort && f.sort !== "new") || f.all);
  var count = (f.subject ? f.subject.length : 0) + (f.series ? 1 : 0) + (f.type ? 1 : 0) + (f.len ? 1 : 0) + (f.sort && f.sort !== "new" ? 1 : 0);
  function qsWithout(key, val) {
    var parts = [];
    if (f.q && key !== "q") { parts.push("q=" + encodeURIComponent(f.q)); }
    (f.subject || []).forEach(function (s) { if (!(key === "subject" && val === s)) { parts.push("subject=" + encodeURIComponent(s)); } });
    [ "series", "type", "len", "sort" ].forEach(function (k) { if (f[k] && k !== key && !(k === "sort" && f[k] === "new")) { parts.push(k + "=" + encodeURIComponent(f[k])); } });
    return "/journal/discover" + (parts.length ? "?" + parts.join("&") : "");
  }
  var chips = [];
  if (f.q) { chips.push([ "q", "", "“" + f.q + "”" ]); }
  (f.subject || []).forEach(function (s) { chips.push([ "subject", s, subjName(s) ]); });
  if (f.series) { chips.push([ "series", "", seriesName(f.series) ]); }
  if (f.type) { chips.push([ "type", "", f.type ]); }
  if (f.len) { chips.push([ "len", "", (LENS.filter(function (l) { return l[0] === f.len; })[0] || [ "", f.len ])[1] ]); }
  if (f.sort && f.sort !== "new") { chips.push([ "sort", "", "Sort: " + (SORTS.filter(function (s) { return s[0] === f.sort; })[0] || [ "", f.sort ])[1] ]); }

  var head = { n: "div", c: "tm-dhead tm-glass", i: { n: "div", c: "tm-card", i: [
    { n: "div", c: "tm-row", i: [ iw("stars"), { n: "span", c: "tm-kicker", i: "Discover" } ] },
    { n: "h1", i: "Find an entry" },
    { n: "form", c: "tm-dbar", role: "search", id: "tm-dform", action: "/journal/discover", method: "get", i: [
      { n: "label", c: "tm-skip", for: "tm-dq", i: "Search The Making" }, searchIcon(),
      { n: "input", id: "tm-dq", type: "search", name: "q", value: f.q || "", placeholder: "Search entries, people, places, ideas", autocomplete: "off" }
    ].concat((f.subject || []).map(function (s) { return { n: "input", type: "hidden", name: "subject", value: s }; }))
     .concat([ "series", "type", "len", "sort" ].filter(function (k) { return f[k]; }).map(function (k) { return { n: "input", type: "hidden", name: k, value: f[k] }; }))
     .concat([ { n: "button", c: "tm-fbtn", type: "button", id: "tm-fopen", "aria-haspopup": "dialog", i: [
        { n: "svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2", "aria-hidden": "true", focusable: "false", i: { n: "path", d: "M4 5h16l-6 7v6l-4 2v-8z" } },
        "Filters" ].concat(count ? [ { n: "b", i: String(count) } ] : []) },
       { n: "button", c: "tm-skip", type: "submit", i: "Search" } ]) },
    chips.length ? { n: "div", c: "tm-fchips", i: chips.map(function (c) {
      return { n: "a", c: "tm-fchip", href: qsWithout(c[0], c[1]), i: [ c[2], { n: "span", "aria-hidden": "true", i: "✕" }, { n: "span", c: "tm-skip", i: "Remove filter" } ] };
    }).concat([ { n: "a", c: "tm-fclear", href: "/journal/discover", i: "Clear all" } ]) } : ""
  ] } };

  var body = [];
  if (active) {
    var r = filterEntries(all, f);
    body.push({ n: "section", c: "tm-sec", id: "tm-results", "aria-live": "polite", i: [ sechead(r.length + " result" + (r.length === 1 ? "" : "s")),
      r.length ? { n: "div", c: "tm-paper tm-glass tm-results", i: { n: "div", c: "tm-grid3", i: r.map(function (e) { return storyCard(e, false); }) } }
        : { n: "div", c: "tm-card tm-empty", i: [ { n: "b", i: "Nothing matches yet." }, { n: "p", c: "tm-meta", i: "Try fewer filters or another word." } ] } ] });
  } else {
    var series = seriesList(all);
    if (series.length) {
      body.push({ n: "section", c: "tm-sec", i: [ sechead("Series"), { n: "div", c: "tm-cards", i: series.map(function (s) {
        return { n: "a", c: "tm-scard tm-scard-tall tm-reveal", href: "/journal/discover?series=" + encodeURIComponent(s.key), i: [
          { n: "img", src: site(s.img), alt: "", loading: "lazy", decoding: "async" },
          { n: "div", c: "tm-cap", i: [ { n: "span", c: "tm-kicker tm-kicker-gold", i: "Series" }, { n: "b", i: s.name },
            { n: "small", i: s.count + (s.of ? " of " + s.of : "") + " published." },
            s.of ? { n: "div", c: "tm-bar2", i: { n: "i", style: "width:" + Math.min(100, Math.round(100 * s.count / s.of)) + "%" } } : "" ] } ] };
      }) } ] });
    }
    body.push({ n: "section", c: "tm-sec tm-sec-tight", i: [ sechead("Subjects"), { n: "div", c: "tm-cards", i: SUBJECTS.map(function (s) {
      var c = all.filter(function (e) { return (e.subjects || []).indexOf(s.key) > -1; }).length;
      return { n: "a", c: "tm-scard tm-reveal", href: "/journal/discover?subject=" + s.key, i: [
        { n: "img", src: bgUrl(s.img), alt: "", loading: "lazy", decoding: "async" },
        { n: "div", c: "tm-cap", i: [ { n: "span", c: "tm-kicker tm-kicker-gold", i: c + (c === 1 ? " entry" : " entries") }, { n: "b", i: s.name } ] } ] };
    }) } ] });
    body.push({ n: "section", c: "tm-sec tm-sec-tight", i: [ sechead("Every entry"),
      { n: "a", c: "tm-btn", href: "/journal/discover?all=1", i: all.length + (all.length === 1 ? " entry" : " entries") + ", newest first →" } ] });
  }

  var filters = { n: "dialog", c: "tm-cf", id: "tm-filter-dlg", "aria-labelledby": "tm-fdh", i: { n: "form", c: "tm-cfi", action: "/journal/discover", method: "get", i: [
    { n: "h2", id: "tm-fdh", i: "Filters" },
    { n: "p", c: "tm-sub", i: "Results match every group you set; choosing more than one subject shows entries in any of them." },
    { n: "div", c: "tm-fbody", i: [
      { n: "div", c: "tm-fgrp tm-fgrp-wide", i: [ { n: "h3", i: "Search terms" }, { n: "input", type: "search", name: "q", value: f.q || "", placeholder: "Words that must match", "aria-label": "Search terms" } ] },
      fgroup("Subjects", "subject", SUBJECTS.map(function (s) { return [ s.key, s.name ]; }), f.subject || [], true),
      seriesList(all).length ? fgroup("Series", "series", seriesList(all).map(function (s) { return [ s.key, s.name ]; }), f.series ? [ f.series ] : [], false) : "",
      fgroup("Type", "type", TYPES.map(function (t) { return [ t, t ]; }), f.type ? [ f.type ] : [], false),
      fgroup("Read time", "len", LENS, f.len ? [ f.len ] : [], false),
      fgroup("Sort", "sort", SORTS, [ f.sort || "new" ], false, true)
    ] },
    { n: "div", c: "tm-actions", i: [ { n: "button", c: "tm-pill", type: "submit", i: "Apply filters" }, { n: "a", c: "tm-pill", href: "/journal/discover", i: "Clear all" } ] },
    { n: "button", c: "tm-circ tm-x", type: "button", "data-close": "true", "aria-label": "Close", i: "✕" }
  ] } };

  var nodes = [ { n: "div", c: "tm-wrap tm-dwrap", i: [ head ].concat(body).concat([ filters ]) } ];
  return shell({ view: "discover", page: 1, banner: bannerInfo, offer: x.navOffer }, nodes, null);
}

/* Native inputs (checkbox / radio) styled as the template's option pills, so the
 * filter dialog is a plain GET form that works without JavaScript. */
function fgroup(title, name, items, chosen, multi, required) {
  return { n: "fieldset", c: "tm-fgrp", i: [ { n: "legend", i: title }, { n: "div", c: "tm-fopts", i: items.map(function (it, i) {
    var id = "tm-f-" + name + "-" + i;
    var input = { n: "input", type: multi ? "checkbox" : "radio", name: name, value: it[0], id: id, c: "tm-fin" };
    if (chosen.indexOf(it[0]) > -1) { input.checked = "checked"; }
    return { n: "span", c: "tm-foptw", i: [ input, { n: "label", c: "tm-fopt", for: id, i: it[1] } ] };
  }).concat(!multi && !required ? [ { n: "span", c: "tm-foptw", i: [
    (function () { var n = { n: "input", type: "radio", name: name, value: "", id: "tm-f-" + name + "-any", c: "tm-fin" }; if (!chosen.length) { n.checked = "checked"; } return n; })(),
    { n: "label", c: "tm-fopt", for: "tm-f-" + name + "-any", i: "Any" } ] } ] : []) } ] };
}

/* ── entry ────────────────────────────────────────────────────────────────── */

function figure(img, assets, i) {
  var url = assets[img.asset] || null;
  if (!url) { return null; }
  var cap = [ img.caption, img.label, img.credit ].filter(Boolean).join(" · ");
  return { n: "figure", c: "tm-fig tm-reveal", i: [
    { n: "button", type: "button", "data-full": url, "data-cap": cap, "aria-label": "View image full screen",
      i: { n: "img", src: url, alt: img.alt || "", width: String(img.width || 1600), height: String(img.height || 900), loading: "lazy", decoding: "async" } },
    cap ? { n: "figcaption", i: cap } : ""
  ] };
}

function blocks(p, assets, offer, slug) {
  var out = [], firstP = true;
  (p.body || []).forEach(function (b, i) {
    if (!b || !b.type) { return; }
    if (b.type === "p") { out.push({ n: "p", c: firstP ? "tm-first" : "", i: inline(b.text) }); firstP = false; return; }
    if (b.type === "h2" || b.type === "h3") { var h = { n: b.type, i: b.text }; if (b.id) { h.id = b.id; } out.push(h); return; }
    if (b.type === "image") { var fg = figure(b.image, assets, i); if (fg) { out.push(fg); } return; }
    if (b.type === "quote") { out.push({ n: "blockquote", c: "tm-quote", i: [ { n: "p", i: inline(b.text) } ].concat(b.cite ? [ { n: "cite", i: b.cite } ] : []) }); return; }
    if (b.type === "pullquote") { out.push({ n: "blockquote", c: "tm-pullquote tm-reveal", i: [ { n: "p", i: inline(b.text) } ].concat(b.cite ? [ { n: "cite", i: b.cite } ] : []) }); return; }
    if (b.type === "list") { out.push({ n: b.ordered ? "ol" : "ul", i: (b.items || []).map(function (it) { return { n: "li", i: inline(it) }; }) }); return; }
    if (b.type === "aside") { out.push({ n: "aside", c: "tm-asideblock", i: inline(b.text) }); return; }
    if (b.type === "hr") { out.push({ n: "hr" }); return; }
    if (b.type === "code") {
      out.push({ n: "figure", c: "tm-code", i: [ { n: "pre", tabindex: "0", i: { n: "code", c: b.language ? "language-" + b.language : "", i: String(b.text || "") } } ]
        .concat(b.caption ? [ { n: "figcaption", i: b.caption } ] : []) });
      return;
    }
    if (b.type === "video") {
      out.push({ n: "figure", c: "tm-video tm-reveal", i: { n: "div", c: "tm-videoframe", i: { n: "iframe",
        src: "https://www.youtube-nocookie.com/embed/" + encodeURIComponent(b.youtube_id) + "?rel=0" + (b.start ? "&start=" + Number(b.start) : ""),
        title: b.title, loading: "lazy", allow: "accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen", allowfullscreen: "allowfullscreen",
        referrerpolicy: "strict-origin-when-cross-origin" } } });
      return;
    }
    if (b.type === "cta") { out.push(ctaBlock(offer, slug)); }
  });
  return out;
}

function entryView(data, bannerInfo) {
  var p = data.payload || {};
  var x = data.extra || {};
  var item = data.item || {};
  var assets = x.assets || {};
  var offer = resolveOffer(x.ctas, p.cta && p.cta.registry_id);
  var heroUrl = p.hero && assets[p.hero.asset] ? assets[p.hero.asset] : FALLBACK_ART;
  var first = p.first_published || null;
  var published = item.first_published_at || item.published_at || null;
  var series = p.series && p.series.key ? p.series : null;
  EDIT_DATE = published ? fdate(published) : "";
  var related = (x.related || []).slice(0, 3);
  var O = S.siteOrigin();
  var url = O + "/journal/" + p.slug;

  var byline = [ { n: "b", i: p.byline || "Justin Kazmierczak" }, { n: "br" } ];
  if (first) {
    byline.push("First published " + fdate(first));
    if (published && isoDay(published) !== first) { byline.push(" · Edited " + fdate(published)); }
  } else if (published) { byline.push(fdate(published)); }
  byline.push(" · " + (p.read_minutes || 1) + " min read");

  var main = [
    { n: "section", c: "tm-ecover", i: [
      { n: "img", src: heroUrl, alt: (p.hero && p.hero.alt) || "", fetchpriority: "high", decoding: "async" },
      { n: "div", c: "tm-wrap", i: { n: "div", c: "tm-lead tm-glass", i: { n: "div", c: "tm-card", i: [
        { n: "div", c: "tm-row", i: [ iw("book"), { n: "span", c: "tm-kicker", i: (first ? "From the archive · " : "") + (p.type || "Essay") } ] },
        { n: "h1", i: p.title },
        { n: "p", i: p.dek || "" },
        { n: "div", c: "tm-meta", i: [ { n: "span", i: p.byline || "Justin Kazmierczak" }, { n: "span", i: "· " + fdate(first || published) },
          { n: "span", i: "· " + (p.read_minutes || 1) + " min read" } ].concat((p.subjects || []).map(chipLink)) },
        p.hero && p.hero.credit ? { n: "p", c: "tm-credit", i: p.hero.credit } : ""
      ] } } }
    ] },
    { n: "div", c: "tm-sheet", i: [
      { n: "div", c: "tm-wrap", i: { n: "article", c: "tm-col", i: [
        series ? { n: "p", c: "tm-snav", i: [ "Part " + series.part + (series.of ? " of " + series.of : "") + " · ",
          { n: "a", href: "/journal/discover?series=" + encodeURIComponent(series.key), i: seriesName(series.key) } ] } : "",
        { n: "div", c: "tm-by", i: [ { n: "img", src: AUTHOR_IMG, alt: "Justin Kazmierczak", width: "46", height: "46" }, { n: "span", i: byline } ] },
        p.method_line ? { n: "p", c: "tm-method", i: p.method_line } : "",
        { n: "div", c: "tm-body", i: blocks(p, assets, offer, p.slug || "") },
        (p.cta && p.cta.end_link === false) ? "" : { n: "a", c: "tm-endlink", href: "/journal#subscribe", "data-tm-open": "subscribe",
          "data-tm-cta": offer.id, i: [ { n: "span", i: "Liked this? Get the next entry in your inbox." }, { n: "b", i: "Get it by email →" } ] }
      ] } },
      related.length ? { n: "section", c: "tm-readnext tm-wrap", i: [ { n: "h2", i: "Read next" },
        { n: "div", c: "tm-grid3", i: related.map(function (e) { return storyCard(e, false); }) } ] } : ""
    ] }
  ];

  var post = { "@type": "BlogPosting", "@id": url + "#post", url: url, headline: p.title, description: p.meta_description || p.dek,
    datePublished: first || isoDay(published), dateModified: isoDay(published) || first, inLanguage: "en-US",
    image: /^\//.test(heroUrl) ? O + heroUrl : heroUrl, wordCount: undefined,
    author: { "@id": O + "/founder#person" }, publisher: { "@id": O + "/#organization" },
    isPartOf: { "@id": O + "/journal#blog" }, mainEntityOfPage: { "@id": url } };
  var schema = S.jsonLd([ post,
    { "@type": "BreadcrumbList", itemListElement: [ { "@type": "ListItem", position: 1, name: "Home", item: O + "/" },
      { "@type": "ListItem", position: 2, name: "The Making", item: O + "/journal" },
      { "@type": "ListItem", position: 3, name: p.title, item: url } ] } ]);
  return shell({ view: "entry", reading: true, banner: bannerInfo, offer: x.navOffer }, main, schema);
}

exports.home = homeView;
exports.discover = discoverView;
exports.entry = entryView;
exports.PER_PAGE = PER_PAGE;
